import {Divider} from '@/components/ui/divider/Divider'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {Text} from '@/components/ui/text/Text'
import {DiagnosisCodeLabel} from '@/features/projects/components/diagnosis-code-label/DiagnosisCodeLabel'
import {ProjectPathologiesDetailsEmptyCard} from '@/features/projects/components/project-pathologies-details-empty-card/ProjectPathologiesDetailsEmptyCard'
import {splitDiagnosisPair} from '@/features/projects/diagnosisVersion'
import {ProjectPathology} from '@/features/projects/types'
import {useTranslation} from '@/translations/i18n'
import dayjs from 'dayjs'
import {Fragment} from 'react'

interface ProjectPathologiesDetailsContentProps {
    pathologies?: (Omit<ProjectPathology, 'startedAt'> & {startedAt?: string})[]
    isLoading: boolean
    isError: boolean
    showStartDate?: boolean
    isMainPathologies?: boolean
}

export const ProjectPathologiesDetailsContent = ({
    pathologies,
    isLoading,
    isError,
    showStartDate,
    isMainPathologies
}: ProjectPathologiesDetailsContentProps) => {
    const {t} = useTranslation()

    return isLoading ? (
        <ProjectPathologiesDetailsEmptyCard
            isLoading
            showStartDate={showStartDate}
            isMainPathologies={isMainPathologies}
        />
    ) : isError ? (
        <Text size="textSm" palette="danger" shade="700" weight="400">
            {t('errors:default')}
        </Text>
    ) : pathologies && pathologies?.length > 0 ? (
        <Flexbox direction="column" gap={2} fullWidth>
            {pathologies.map((pathology, index) => {
                // Only one code is stored: the other classification is rebuilt from its mapped
                // codes, so read mode shows the same pair the form edits.
                const {icd9, icd10} = splitDiagnosisPair(pathology.pathology)

                return (
                    <Fragment key={pathology.id}>
                        <Grid columns={4} gap={2} fullWidth>
                            <GridItem spanColumn={3} direction="column" gap={2}>
                                <Flexbox direction="column" gap={2}>
                                    <Text size="textSm" palette="neutral" shade="500" weight="400">
                                        {t('projects:pathology_number', {number: index + 1})}
                                    </Text>
                                    <Flexbox direction="column" gap={1}>
                                        <DiagnosisCodeLabel diagnosis={icd9} />
                                        <DiagnosisCodeLabel diagnosis={icd10} />
                                    </Flexbox>
                                </Flexbox>
                            </GridItem>
                            {showStartDate && (
                                <GridItem direction="column" gap={2}>
                                    <Text size="textSm" palette="neutral" shade="500" weight="400">
                                        {t('projects:start_date')}
                                    </Text>
                                    {pathology.startedAt ? (
                                        <Text size="textSm" weight="500">
                                            {dayjs(pathology.startedAt).format('DD/MM/YYYY')}
                                        </Text>
                                    ) : (
                                        <Text size="textSm" weight="500">
                                            -
                                        </Text>
                                    )}
                                </GridItem>
                            )}
                        </Grid>
                        {index != pathologies.length - 1 && <Divider direction="horizontal" width="100%" />}
                    </Fragment>
                )
            })}
        </Flexbox>
    ) : (
        <ProjectPathologiesDetailsEmptyCard showStartDate={showStartDate} isMainPathologies={isMainPathologies} />
    )
}
